import './workload-panels.css';

/**
 * One member's row: a proportional stacked bar, comparable across the panel.
 *
 * Ported from the predecessor's `StackedRow`, and the geometry is the
 * design. **The bar's length is `total / scaleMax` and the segments divide
 * that length**, so a row carries two facts at once: how much (against the
 * busiest row in the panel) and of what. Normalising every bar to full
 * width would show composition and lose magnitude; drawing segments at
 * absolute widths would show magnitude and make composition unreadable on
 * the quiet rows.
 *
 * **A zero row stays visible and goes grey.** Dropping it would make the
 * panel a list of the active, and "this person did none of this" is a fact
 * the panel exists to show — Andrew has zero commits and 45 Jira actions on
 * his own tickets, which is a role, not an absence of data.
 *
 * A minimum of 2% width for any non-zero total, so one commit against a
 * busiest row of 573 is a mark rather than nothing.
 */

export interface Segment {
  label: string;
  value: number;
  color: string;
}

export interface Badge {
  label: string;
  tone: 'warn' | 'info' | 'muted';
  title?: string;
}

interface Props {
  name: string;
  role?: string | null;
  segments: Segment[];
  /** The panel's busiest total, so rows are comparable. */
  scaleMax: number;
  totalLabel?: string;
  badges?: Badge[];
  subtitle?: string;
  onSelect?: () => void;
  selected?: boolean;
}

export function StackedRow({
  name,
  role,
  segments,
  scaleMax,
  totalLabel,
  badges,
  subtitle,
  onSelect,
  selected,
}: Props) {
  const total = segments.reduce((a, s) => a + s.value, 0);
  const pct = scaleMax > 0 ? (total / scaleMax) * 100 : 0;
  const empty = total === 0;

  return (
    <button
      type="button"
      className={`wlr${empty ? ' is-empty' : ''}${selected ? ' is-selected' : ''}`}
      onClick={onSelect}
      disabled={!onSelect}
      title={`${name}${role ? ` · ${role}` : ''}`}
    >
      <span className="wlr__who">
        <span className="wlr__name">{name}</span>
        {badges?.map((b) => (
          <span
            key={b.label}
            className={`wlr__badge wlr__badge--${b.tone}`}
            title={b.title ?? b.label}
          >
            {b.label}
          </span>
        ))}
        {subtitle && (
          <span className="wlr__subtitle" title={subtitle}>
            {subtitle}
          </span>
        )}
      </span>

      <span className="wlr__track">
        {!empty && (
          <span className="wlr__bar" style={{ width: `${Math.max(pct, 2)}%` }}>
            {segments
              .filter((s) => s.value > 0)
              .map((s) => (
                <span
                  key={s.label}
                  className="wlr__seg"
                  style={{
                    width: `${(s.value / total) * 100}%`,
                    backgroundColor: s.color,
                  }}
                  title={`${s.label}: ${s.value}`}
                />
              ))}
          </span>
        )}
      </span>

      <span className="wlr__total">{totalLabel ?? total}</span>
    </button>
  );
}

/** The panel's legend. Segment order and colour must match the bars. */
export function Legend({ segments }: { segments: Segment[] }) {
  return (
    <span className="wll">
      {segments.map((s) => (
        <span className="wll__chip" key={s.label}>
          <span className="wll__dot" style={{ backgroundColor: s.color }} />
          {s.label}
        </span>
      ))}
    </span>
  );
}
